/**
 * Onboarding page 3 — « Vos prompts » (the Prompt Studio composer, onboarding-shaped).
 *
 * On a brand with no prompt yet, a STARTER BATCH is generated per axis (`regenerate` WITHOUT
 * `studio`: the studio's 10 s per-brand cooldown would 429 three of four parallel axes),
 * classified (`bulk` dryRun), fitted to the monthly allowance (the cadence slows before the
 * batch shrinks, owner 2026-11-03), then saved (`bulk`). Everything shown is persisted:
 * the bin deletes, the zone chip PATCHes, « + Ajouter des prompts » is the studio composer +
 * review, and the usage bar + cadence squares read the server's allowances.
 *
 * « Lancer » runs the PROMPTS only — never the website / earned-media audits (owner):
 * `POST /audits brand_<axis>` per axis that has prompts. That is the one first-run path
 * every tier has; a composer save never dispatches anything by itself (POST bulk never
 * calls FirstRunService).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Card, CircularProgress, IconButton, LinearProgress } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { Brand, BrandPersona, BrandQuery, BrandQueryAxis, BulkItem, Cadence, GscCallbackResult, TargetZone } from '@traaker/shared';
import { effectiveFeatureFlags, promptPlaceKey, zoneDisplayLabel } from '@traaker/shared';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../../services/api';
import { brandQueriesApi } from '../../services/brand-queries';
import { useAuth } from '../../contexts/AuthContext';
import { pushDataLayerEvent } from '../../utils/analytics';
import { formatNumber } from '../../i18n/format';
import { languageFlag } from '../dashboard/geoFlags';
import { Flag } from '../dashboard/Flag';
import { conversationsApi } from '../../components/prompt-studio/conversations/conversationsApi';
import {
  AXIS_COLOR,
  AXIS_SPLIT_LIVE,
  MONDIAL,
  batchCalls,
  groupFamilies,
  knownZones,
  offeredAxes,
  promptPickerZones,
  type PromptAllowanceFigures,
} from '../../components/prompt-studio/promptStudioModel';
import { PromptStudioComposer, SOURCE_LABELS, type ComposerState } from '../../components/prompt-studio/PromptStudioComposer';
import { emptyComposer, languageLabel } from '../../components/prompt-studio/PromptStudioView';
import { ReviewTable } from '../../components/prompt-studio/ReviewTable';
import type { ReviewRow } from '../../components/prompt-studio/studioReducer';
import { classifyDraft, classifyRequest, SOURCE_TO_BULK, toReviewRow, type ComposerSource } from '../../components/prompt-studio/composerModel';
import { FooterSettingsPanel, type AgencyFooterFigures, type FooterChange } from '../../components/prompt-studio/FooterSettingsPanel';
import { loadAgencyFigures } from '../../components/prompt-studio/PromptStudioDialog';
import { LocChip } from '../../components/prompt-studio/PromptRow';
import { Pick } from '../../components/prompt-studio/Pick';
import {
  DEFAULT_AGENCY_ENGINES,
  STARTER_COUNTS,
  fastestAffordableAgencyCadence,
  fastestFittingCadence,
  maxRowsAtSlowest,
  sameCadence,
  trimBalanced,
} from './onboardingModel';
import { InfoTip, QuitOnboardingButton } from './onboardingShared';

const AXIS_KEY: Record<BrandQueryAxis, string> = {
  narrative: 'onboarding.axis.narrative',
  authority: 'onboarding.axis.authority',
  referral: 'onboarding.axis.referral',
  comparison: 'onboarding.axis.comparison',
};

type Starter = 'idle' | 'running' | 'failed' | 'done';
/** `GET .../allowances` promptRequests: the server always returns the cadence (FooterStrip reads it the same way). */
type StudioFigures = PromptAllowanceFigures & { cadence: Cadence; creditsPerMonth?: number };
/** `GET /metering/usage` as loadAgencyFigures reads it. */
type AgencyUsageReadout = Awaited<ReturnType<Parameters<typeof loadAgencyFigures>[2]>>;

export function PromptsStep({
  brandId,
  brand,
  onFinish,
  onBack,
  canQuit,
  onQuit,
  gscReturn,
}: {
  brandId: string;
  brand: Brand;
  onFinish: () => Promise<void>;
  /** « ← Précédent »: back to « Votre marché ». */
  onBack: () => void;
  canQuit: boolean;
  onQuit: () => Promise<void>;
  /** Back from the Search Console consent: open « + Ajouter des prompts » on its source (the band already has the result). */
  gscReturn?: GscCallbackResult | null;
}) {
  const { t, i18n } = useTranslation();
  const { tenant } = useAuth();
  const isAgency = tenant?.tier === 'agency';
  const convFlag = !!tenant && !!effectiveFeatureFlags(tenant.tier, tenant.settings?.featureFlags).conversationsEnabled;
  const strategy = brand.strategy;
  const workingLanguage = (strategy?.workingLanguage || 'fr').toLowerCase();
  const axes = offeredAxes(AXIS_SPLIT_LIVE);

  const [queries, setQueries] = useState<BrandQuery[] | null>(null);
  const [figures, setFigures] = useState<StudioFigures | null>(null);
  const [agencyFigures, setAgencyFigures] = useState<AgencyFooterFigures | undefined>(undefined);
  const [personas, setPersonas] = useState<BrandPersona[] | null>(null);
  const [starter, setStarter] = useState<Starter>('idle');
  const [error, setError] = useState<string | null>(null);
  const [busyRow, setBusyRow] = useState<string | null>(null);
  const [zonePick, setZonePick] = useState<{ queryId: string; query: string } | null>(null);
  const [launching, setLaunching] = useState(false);
  const [launchFailed, setLaunchFailed] = useState<BrandQueryAxis[]>([]);
  // The brand row passed in is not refreshed on this page: an engine write is shown from what was written
  // (PromptStudioDialog's `footerEngines`), else the tiles never move.
  const [engines, setEngines] = useState<string[] | undefined>(brand.auditConfig?.enginesEnabled);

  // ── Data ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    const [list, fig] = await Promise.all([brandQueriesApi.list(brandId), brandQueriesApi.allowances(brandId)]);
    const rows = (list as BrandQuery[]).filter((q) => !(q.status === 'archived' && q.archivedReason === 'deleted'));
    setQueries(rows);
    const promptFigures = (fig as { promptRequests: StudioFigures }).promptRequests;
    setFigures(promptFigures);
    let agency: AgencyFooterFigures | undefined;
    if (isAgency) {
      agency = (await loadAgencyFigures(tenant, brandId, (path) => api.get<AgencyUsageReadout>(path)).catch(() => undefined)) ?? undefined;
      setAgencyFigures(agency);
    }
    return { rows, promptFigures, agency };
  }, [brandId, isAgency, tenant]);

  // Personas: the composer's « Persona » source shows only when the brand has some (lot I gate).
  useEffect(() => {
    if (!convFlag) return;
    conversationsApi.brandPersonas(brandId).then((p) => setPersonas(p ?? []), () => setPersonas([]));
  }, [brandId, convFlag]);

  // ── Starter batch ─────────────────────────────────────────────────────────
  const runStarter = useCallback(async () => {
    setStarter('running');
    setError(null);
    try {
      const generated = await Promise.allSettled(
        axes.map((axis) =>
          brandQueriesApi.regenerate(brandId, axis, { count: STARTER_COUNTS[axis], languages: [workingLanguage], zones: [] }),
        ),
      );
      const texts = [...new Set(generated.flatMap((r) => (r.status === 'fulfilled' ? r.value.proposals.map((p) => p.text) : [])).filter(Boolean))];
      if (texts.length === 0) throw new Error(t('onboarding.promptsStep.starterFailed'));

      // Classification = « Vérifier » (axis, intent, language, topic per line).
      const classified = await brandQueriesApi.bulk(brandId, classifyRequest(texts.slice(0, 100), 'gen', null));
      let rows: ReviewRow[] = classified.dryRun ? classified.items.map(toReviewRow) : [];
      if (rows.length === 0) throw new Error(t('onboarding.promptsStep.starterFailed'));
      const source = SOURCE_TO_BULK.gen;
      const items = (rs: ReviewRow[]): BulkItem[] =>
        rs.map((r) => ({ text: r.text, intent: r.intent as BulkItem['intent'], language: r.language, complements: r.complements as BulkItem['complements'], topic: r.topic, criteria: r.criteria, targetId: r.targetId }));

      const { promptFigures } = await load();
      // Self-serve: land the batch on the fastest cadence the allowance sustains, BEFORE the save
      // (a save that does not fit is refused whole, 409 ALLOWANCE_EXCEEDED).
      if (!isAgency && promptFigures && promptFigures.allowance !== -1) {
        const preview = await brandQueriesApi.bulk(brandId, { items: items(rows), dryRun: true, source, defaults: { zoneMeta: null } });
        const perLaunch = preview.figuresAfter?.perLaunch ?? 0;
        let cadence = fastestFittingCadence(perLaunch, promptFigures.allowance);
        if (!cadence) {
          rows = trimBalanced(rows, maxRowsAtSlowest(perLaunch / rows.length, promptFigures.allowance));
          cadence = { interval: 7, unit: 'days' };
        }
        if (!sameCadence(cadence, promptFigures.cadence)) {
          await api.patch(`/brands/${brandId}/audit-config`, { promptCadence: cadence });
        }
      }
      // Agency: Perplexity, ChatGPT and AI Overview by default (owner 2026-11-03), before the save so the
      // cadence below is fitted on them. Self-serve engines are the tier's (the server keeps a choice for agency only).
      if (isAgency && !brand.auditConfig?.enginesEnabled?.length) {
        await api.patch(`/brands/${brandId}/audit-config`, { enginesEnabled: DEFAULT_AGENCY_ENGINES });
        setEngines(DEFAULT_AGENCY_ENGINES);
      }
      try {
        await brandQueriesApi.bulk(brandId, { items: items(rows), dryRun: false, source, defaults: { zoneMeta: null } });
      } catch (err) {
        // The preview's figures can be missing or stale: a refusal gets ONE retry at the
        // slowest square with half the batch, never a loop.
        if (!(err instanceof ApiError && err.status === 409 && err.body?.code === 'ALLOWANCE_EXCEEDED')) throw err;
        await api.patch(`/brands/${brandId}/audit-config`, { promptCadence: { interval: 7, unit: 'days' } });
        rows = trimBalanced(rows, Math.floor(rows.length / 2));
        await brandQueriesApi.bulk(brandId, { items: items(rows), dryRun: false, source, defaults: { zoneMeta: null } });
      }

      const after = await load();
      // Agency: the fastest cadence the credit balance affords (a new agency brand starts on « Manuel »).
      if (isAgency && after.promptFigures && after.agency) {
        const cadence = fastestAffordableAgencyCadence(after.promptFigures.perLaunch, after.agency.availableCredits);
        if (!sameCadence(cadence, after.promptFigures.cadence)) {
          await api.patch(`/brands/${brandId}/audit-config`, { promptCadence: cadence });
          await load();
        }
      }
      setStarter('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStarter('failed');
      await load().catch(() => void 0);
    }
  }, [axes, brand.auditConfig?.enginesEnabled, brandId, isAgency, load, t, workingLanguage]);

  // On entry: load; a brand with no prompt gets the starter batch (a resumed one keeps its own).
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    load()
      .then(({ rows }) => {
        if (!rows.some((q) => q.status === 'active')) void runStarter();
        else setStarter('done');
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [load, runStarter]);

  // ── List ──────────────────────────────────────────────────────────────────
  const locations = brand.locations ?? [];
  const known = useMemo(() => knownZones(queries ?? [], [], locations), [queries, locations]);
  const placeZones = useMemo(() => promptPickerZones(known, locations), [known, locations]);
  const families = useMemo(() => groupFamilies((queries ?? []).filter((q) => q.status === 'active')), [queries]);
  const groups = useMemo(() => {
    const order: Array<BrandQueryAxis | 'pending'> = [...axes, 'pending'];
    return order
      .map((axis) => ({ axis, families: families.filter((f) => f.root.axis === axis) }))
      .filter((g) => g.families.length > 0);
  }, [axes, families]);
  const activeAxes = groups.map((g) => g.axis).filter((a): a is BrandQueryAxis => a !== 'pending');
  const promptCount = families.length;

  const removeFamily = async (rootId: string) => {
    const family = families.find((f) => f.root.queryId === rootId);
    if (!family) return;
    setBusyRow(rootId);
    setError(null);
    try {
      for (const c of batchCalls(family, 'delete')) await brandQueriesApi.archive(brandId, c.axis, c.queryId, c.reason);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyRow(null);
    }
  };

  const setRowZone = async (queryId: string, zone: TargetZone | null) => {
    const row = queries?.find((q) => q.queryId === queryId);
    setZonePick(null);
    if (!row) return;
    setBusyRow(queryId);
    setError(null);
    try {
      await brandQueriesApi.update(brandId, queryId, { axis: row.axis as BrandQueryAxis | 'pending', zoneMeta: zone });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyRow(null);
    }
  };

  // ── Composer + review (the PromptStudioView glue) ─────────────────────────
  const [composerOpen, setComposerOpen] = useState(!!gscReturn);
  const genLanguages = useMemo(() => [{ code: workingLanguage, label: languageLabel(workingLanguage, i18n.language) }], [workingLanguage, i18n.language]);
  const vocabByAxis = useMemo(
    () => ({
      narrative: strategy?.decisionCriteria ?? [],
      authority: strategy?.keywords ?? [],
      referral: strategy?.scenarios ?? [],
      comparison: strategy?.decisionCriteria ?? [],
    }),
    [strategy],
  );
  const [composer, setComposer] = useState<ComposerState>(() => ({ ...emptyComposer('authority', workingLanguage), source: gscReturn ? 'gsc' : 'gen' }));
  const [review, setReview] = useState<{ rows: ReviewRow[]; source: ComposerSource; rateLimited: number } | null>(null);
  const reviewRowsRef = useRef<ReviewRow[]>([]);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const handleVerify = async (lines: string[], source: ComposerSource) => {
    if (verifying) return;
    setVerifying(true);
    setVerifyError(null);
    const res = await classifyDraft(lines, async (texts) => {
      const r = await brandQueriesApi.bulk(brandId, classifyRequest(texts, source, composer.zone));
      return r.dryRun ? r.items : [];
    });
    setVerifying(false);
    if ('error' in res) {
      setVerifyError(res.error === 'rate_limited' ? t('promptStudio.composer.rateLimited') : t('onboarding.promptsStep.verifyFailed'));
      return;
    }
    reviewRowsRef.current = res.rows;
    setReview({ rows: res.rows, source, rateLimited: res.rateLimited });
  };

  const handleReviewChange = (rows: ReviewRow[]) => {
    reviewRowsRef.current = rows;
    setReview((r) => r && { ...r, rows, rateLimited: Math.min(r.rateLimited, rows.filter((x) => !x.intent).length) });
  };

  const handleRetry = async (row: ReviewRow) => {
    if (!review) return;
    const rowZone = row.zoneMeta !== undefined ? (row.zoneMeta as TargetZone | null) : composer.zone;
    try {
      const r = await brandQueriesApi.bulk(brandId, classifyRequest([row.text], review.source, rowZone));
      const item = r.dryRun ? r.items[0] : undefined;
      if (item) {
        const next: ReviewRow = { ...toReviewRow(item), ...(row.zoneMeta !== undefined ? { zoneMeta: row.zoneMeta } : {}) };
        handleReviewChange(reviewRowsRef.current.map((x) => (x === row ? next : x)));
      }
    } catch (err) {
      if ((err as { status?: number })?.status === 429) setReview((r) => r && { ...r, rateLimited: Math.max(r.rateLimited, 1) });
    }
  };

  const handleCreated = () => {
    void load();
    // Rows the server refused stay in the review; a clean save closes the composer.
    if (reviewRowsRef.current.length === 0) {
      setReview(null);
      setComposer((c) => ({ ...emptyComposer('authority', workingLanguage), source: 'gen', cooldownUntil: c.cooldownUntil }));
      setComposerOpen(false);
    }
  };

  // ── Launch ────────────────────────────────────────────────────────────────
  const launch = async (onlyAxes?: BrandQueryAxis[]) => {
    setLaunching(true);
    setError(null);
    const targets = onlyAxes ?? activeAxes;
    const results = await Promise.allSettled(
      targets.map((axis) => api.post('/audits', { type: `brand_${axis}`, config: {}, brandId })),
    );
    const failed = targets.filter((_, i) => results[i].status === 'rejected');
    const firstError = results.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
    setLaunchFailed(failed);
    if (failed.length > 0) {
      const e = firstError?.reason;
      setError(e instanceof ApiError || e instanceof Error ? e.message : t('onboarding.promptsStep.launchFailed'));
      setLaunching(false);
      return;
    }
    await finish();
  };

  const finish = async () => {
    setLaunching(true);
    // Write-once marker: the app stops routing this brand back into the wizard.
    await api.post(`/brands/${brandId}/complete-onboarding`, {}).catch(() => void 0);
    pushDataLayerEvent('onboarding_complete');
    await onFinish();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const zoneRow = zonePick ? queries?.find((q) => q.queryId === zonePick.queryId) : undefined;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ borderRadius: 4, pb: 1 }}>
        {/* « + Ajouter des prompts », a closed dropdown (owner 2026-11-03) */}
        <Box
          component="button"
          type="button"
          onClick={() => setComposerOpen((o) => !o)}
          disabled={starter === 'running'}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, width: '100%', px: 3, py: 2, border: 'none', background: 'none',
            cursor: 'pointer', color: 'primary.main', fontWeight: 600, fontSize: 14, fontFamily: 'inherit', textAlign: 'left',
            '&:disabled': { color: 'text.disabled', cursor: 'default' },
          }}
        >
          {t('onboarding.promptsStep.add')}
          <Box component="span" sx={{ transition: 'transform .2s', transform: composerOpen ? 'rotate(180deg)' : 'none' }}>⌄</Box>
        </Box>

        {composerOpen && (
          <Box sx={{ mx: 2, mb: 1.5, border: '1px solid', borderColor: 'rgba(124,58,237,.22)', borderRadius: 3, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: 640 }}>
            {(verifyError || verifying) && (
              <Box sx={{ px: 2, py: 0.75, fontSize: 12, bgcolor: verifyError ? '#fffbeb' : 'transparent', color: verifyError ? '#b45309' : 'text.secondary' }}>
                {verifyError ?? t('onboarding.promptsStep.verifying')}
              </Box>
            )}
            {review ? (
              <ReviewTable
                rows={review.rows}
                source={SOURCE_LABELS[review.source]}
                bulkSource={SOURCE_TO_BULK[review.source]}
                bulkDefaults={{ zoneMeta: composer.zone }}
                placeZones={placeZones}
                lang={workingLanguage}
                mode="batch"
                brandId={brandId}
                isAgency={isAgency}
                currentFigures={figures}
                monthFigure={null}
                rateLimited={review.rateLimited}
                firstRun={null}
                onChange={handleReviewChange}
                onRetry={(row) => void handleRetry(row)}
                onBack={() => setReview(null)}
                onCreated={handleCreated}
                criteriaOptions={strategy?.decisionCriteria ?? []}
                topicOptions={{ authority: strategy?.keywords ?? [], referral: strategy?.scenarios ?? [] }}
              />
            ) : (
              <PromptStudioComposer
                state={composer}
                brandId={brandId}
                axes={axes}
                vocabByAxis={vocabByAxis}
                languages={genLanguages}
                personas={personas}
                figures={isAgency ? null : figures}
                locations={locations}
                placeZones={placeZones}
                lang={workingLanguage}
                onChange={(next) => setComposer((c) => ({ ...c, ...next }))}
                onVerifier={(lines, source) => void handleVerify(lines, source)}
                onCancel={() => setComposerOpen(false)}
                searchConsole={{ isAgency, brandUrl: brand.url }}
              />
            )}
          </Box>
        )}

        {starter === 'running' && (
          <Box sx={{ px: 3, py: 5, textAlign: 'center' }}>
            <CircularProgress size={28} sx={{ mb: 1.5 }} />
            <Box sx={{ fontWeight: 600 }}>{t('onboarding.promptsStep.starterRunning')}</Box>
          </Box>
        )}
        {starter === 'failed' && promptCount === 0 && (
          <Box sx={{ px: 3, py: 3, textAlign: 'center' }}>
            <Button variant="outlined" onClick={() => void runStarter()}>{t('onboarding.prompts.review.retry')}</Button>
          </Box>
        )}

        {groups.map((g) => (
          <Box key={g.axis}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, pt: 1.75, pb: 0.75, fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.secondary' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: g.axis === 'pending' ? 'warning.main' : AXIS_COLOR[g.axis] }} />
              {g.axis === 'pending' ? t('onboarding.promptsStep.pending') : t(AXIS_KEY[g.axis])}
              {g.axis !== 'pending' && <InfoTip title={t(`onboarding.promptsStep.axisInfo.${g.axis}`)} />}
              <Box component="span" sx={{ ml: 'auto', textTransform: 'none', letterSpacing: 0, fontWeight: 500, color: 'text.disabled' }}>{g.families.length}</Box>
            </Box>
            {g.families.map((f) => {
              const q = f.root;
              const busy = busyRow === q.queryId;
              return (
                <Box key={q.queryId} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 3, py: 0.75, borderTop: '1px solid', borderColor: '#f6f6f7', fontSize: 13.5, opacity: busy ? 0.5 : 1, '&:hover .row-bin': { opacity: 1 } }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>{q.text}</Box>
                  <LocChip zone={q.zoneMeta ?? null} legacyZone={q.zone} onClick={busy ? undefined : () => setZonePick({ queryId: q.queryId, query: '' })} />
                  <Box sx={{ width: 24, display: 'flex', justifyContent: 'center', fontSize: 12, color: 'text.secondary' }}>
                    {q.language && (languageFlag(q.language)
                      ? <Flag countryCode={languageFlag(q.language)} size={18} title={languageLabel(q.language, i18n.language)} />
                      : <span>{q.language.toUpperCase()}</span>)}
                  </Box>
                  <IconButton
                    className="row-bin"
                    size="small"
                    aria-label={t('onboarding.common.remove')}
                    disabled={busy}
                    onClick={() => void removeFamily(q.queryId)}
                    sx={{ opacity: 0.45, color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: '#fef2f2' } }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        ))}
      </Card>

      {/* The row place picker (one at a time) */}
      <Pick
        open={!!zonePick}
        kind="place"
        selected={zoneRow?.zoneMeta ? promptPlaceKey(zoneRow.zoneMeta) : MONDIAL}
        onToggle={(key) => zonePick && void setRowZone(zonePick.queryId, key === MONDIAL ? null : placeZones.get(key) ?? null)}
        onPickZone={(zone) => zonePick && void setRowZone(zonePick.queryId, zone)}
        onClose={() => setZonePick(null)}
        placeOptions={[...placeZones].map(([key, z]) => ({ key, zone: z, label: zoneDisplayLabel(z) }))}
        searchQuery={zonePick?.query ?? ''}
        onSearchChange={(query) => setZonePick((p) => p && { ...p, query })}
        lang={workingLanguage}
      />

      <UsageCard
        figures={figures}
        agencyFigures={agencyFigures}
        tier={tenant?.tier ?? 'basic'}
        brand={brand}
        enginesEnabled={engines}
        tenantEngines={tenant?.settings?.enabledEngines}
        onSaved={(change) => {
          if (change.enginesEnabled) setEngines(change.enginesEnabled);
          void load();
        }}
      />

      <Card sx={{ borderRadius: 4, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2 }}>
          <Button variant="outlined" color="inherit" onClick={onBack} disabled={launching} sx={{ borderRadius: 3, borderColor: 'divider' }}>
            ← {t('onboarding.common.back')}
          </Button>
          {canQuit && <QuitOnboardingButton brandName={brand.name} onQuit={onQuit} />}
          <Box sx={{ flex: 1 }} />
          {launchFailed.length > 0 && (
            <Button variant="text" onClick={() => void finish()} disabled={launching}>{t('onboarding.promptsStep.finishAnyway')}</Button>
          )}
          <Button
            variant="contained"
            size="large"
            disabled={launching || starter === 'running' || promptCount === 0}
            onClick={() => void launch(launchFailed.length > 0 ? launchFailed : undefined)}
            sx={{ px: 4, borderRadius: 3 }}
          >
            {launching ? <CircularProgress size={20} color="inherit" /> : launchFailed.length > 0 ? t('onboarding.promptsStep.retryLaunch') : t('onboarding.promptsStep.launch', { count: promptCount })}
          </Button>
        </Box>
      </Card>
    </Box>
  );
}

/** Monthly usage (bar) with the cadence squares under it — FooterSettingsPanel inline. */
function UsageCard({
  figures,
  agencyFigures,
  tier,
  brand,
  enginesEnabled,
  tenantEngines,
  onSaved,
}: {
  figures: StudioFigures | null;
  agencyFigures?: AgencyFooterFigures;
  tier: string;
  brand: Brand;
  enginesEnabled?: string[];
  tenantEngines?: string[];
  onSaved: (change: FooterChange) => void;
}) {
  const { t } = useTranslation();
  const agency = tier === 'agency';
  if (!figures || (agency && !agencyFigures)) return null;
  const need = agency ? agencyFigures!.promptCreditsPerMonth : figures.sustainableNeed;
  const cap = agency ? agencyFigures!.availableCredits : figures.allowance;
  const unlimited = cap === -1;
  const over = !unlimited && need > cap;
  const pct = unlimited || cap <= 0 ? 0 : Math.min(100, (need / cap) * 100);
  return (
    <Card sx={{ borderRadius: 4, mt: 2, overflow: 'hidden' }}>
      {!unlimited && (
        <Box sx={{ px: 3, pt: 2.25 }}>
          <LinearProgress
            variant="determinate"
            value={pct}
            color={over ? 'warning' : 'primary'}
            sx={{ height: 8, borderRadius: 4, bgcolor: '#f1f1f3' }}
          />
          <Box sx={{ display: 'flex', fontSize: 12, color: 'text.secondary', mt: 0.75 }}>
            <Box component="span">
              <Box component="b" sx={{ color: over ? 'warning.dark' : 'text.primary' }}>{formatNumber(need)}</Box>{' '}
              {agency ? t('onboarding.promptsStep.creditsPerMonth') : t('onboarding.promptsStep.requestsPerMonth')}
            </Box>
            <Box sx={{ flex: 1 }} />
            {agency ? t('onboarding.promptsStep.balance', { n: formatNumber(cap) }) : t('onboarding.promptsStep.allowance', { n: formatNumber(cap) })}
          </Box>
        </Box>
      )}
      <FooterSettingsPanel
        figures={figures}
        agencyFigures={agencyFigures}
        tier={tier}
        brandId={brand.brandId}
        enginesEnabled={enginesEnabled}
        tenantEnginesEnabled={tenantEngines}
        onSaved={onSaved}
      />
    </Card>
  );
}
